// v10/src/hooks/useOfflineSync.js
import { useState, useEffect, useCallback } from 'react';

const DB_NAME    = 'condor360_offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending_uploads';

// ── IndexedDB helpers ─────────────────────────────────────────────
const openDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB_NAME, DB_VERSION);
  req.onupgradeneeded = (e) => {
    const db = e.target.result;
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      store.createIndex('status', 'status', { unique: false });
    }
  };
  req.onsuccess = (e) => resolve(e.target.result);
  req.onerror   = (e) => reject(e.target.error);
});

const dbAdd = async (item) => {
  const db   = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.add({ ...item, status: 'pending', createdAt: Date.now() });
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
};

const dbGetPending = async () => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('status');
    const req   = index.getAll('pending');
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
};

const dbMarkDone = async (id, cloudinaryUrl) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const get   = store.get(id);
    get.onsuccess = (e) => {
      const item = e.target.result;
      if (item) {
        item.status       = 'done';
        item.cloudinaryUrl = cloudinaryUrl;
        const put = store.put(item);
        put.onsuccess = () => resolve();
        put.onerror   = (e2) => reject(e2.target.error);
      } else resolve();
    };
    get.onerror = (e) => reject(e.target.error);
  });
};

const dbMarkError = async (id) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const get   = store.get(id);
    get.onsuccess = (e) => {
      const item = e.target.result;
      if (item) {
        item.status   = 'error';
        item.attempts = (item.attempts || 0) + 1;
        const put = store.put(item);
        put.onsuccess = () => resolve();
        put.onerror   = (e2) => reject(e2.target.error);
      } else resolve();
    };
    get.onerror = (e) => reject(e.target.error);
  });
};

// ── Hook principal ────────────────────────────────────────────────
export const useOfflineSync = () => {
  const [isOnline,      setIsOnline]      = useState(navigator.onLine);
  const [pendingCount,  setPendingCount]  = useState(0);
  const [syncing,       setSyncing]       = useState(false);

  const getToken = () => {
    try {
      const raw = localStorage.getItem('condor360_current_user')
               || sessionStorage.getItem('condor360_current_user');
      return JSON.parse(raw)?.token || '';
    } catch { return ''; }
  };

  const refreshPendingCount = useCallback(async () => {
    try {
      const pending = await dbGetPending();
      setPendingCount(pending.length);
    } catch { /* ignore */ }
  }, []);

  // ── Guardar foto localmente (funciona offline) ────────────────
  const savePhotoLocally = useCallback(async ({ image, mediaType, aircraftId, itemKey, context }) => {
    try {
      const id = await dbAdd({ image, mediaType, aircraftId, itemKey, context });
      await refreshPendingCount();
      return { localId: id, url: `local://${id}` };
    } catch (err) {
      console.error('[OfflineSync] savePhotoLocally error:', err);
      throw err;
    }
  }, [refreshPendingCount]);

  // ── Sincronizar pendientes cuando hay conexión ────────────────
  const syncPending = useCallback(async () => {
    if (!navigator.onLine || syncing) return;
    const pending = await dbGetPending();
    if (pending.length === 0) return;

    setSyncing(true);
    const API   = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const token = getToken();

    for (const item of pending) {
      // Máximo 3 intentos
      if ((item.attempts || 0) >= 3) continue;
      try {
        const res = await fetch(`${API}/api/images/inspection/upload`, {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            image:      item.image,
            mediaType:  item.mediaType,
            aircraftId: item.aircraftId,
          }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        await dbMarkDone(item.id, data.data.url);
        console.log(`[OfflineSync] Synced photo ${item.id} → ${data.data.url}`);
      } catch (err) {
        console.warn(`[OfflineSync] Failed to sync photo ${item.id}:`, err.message);
        await dbMarkError(item.id);
      }
    }

    await refreshPendingCount();
    setSyncing(false);
  }, [syncing, refreshPendingCount]);

  // ── Cache de imágenes de referencia al elegir avión ──────────
  const cacheReferenceImages = useCallback(async (aircraftId) => {
    if (!navigator.onLine || !('caches' in window)) return;
    try {
      const API   = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = getToken();
      const res   = await fetch(`${API}/api/images/reference/${aircraftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const { data } = await res.json();
      if (!data?.length) return;

      const cache = await caches.open(`condor360-ref-${aircraftId}`);
      const urls  = data.map(img => img.url);
      await cache.addAll(urls);
      console.log(`[OfflineSync] Cached ${urls.length} reference images for ${aircraftId}`);
    } catch (err) {
      console.warn('[OfflineSync] cacheReferenceImages error:', err.message);
    }
  }, []);

  // ── Listeners de conectividad ─────────────────────────────────
  useEffect(() => {
    const onOnline  = () => { setIsOnline(true);  syncPending(); };
    const onOffline = () => setIsOnline(false);

    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    refreshPendingCount();

    // Intentar sync al montar si ya hay conexión
    if (navigator.onLine) syncPending();

    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [syncPending, refreshPendingCount]);

  return {
    isOnline,
    syncing,
    pendingCount,
    savePhotoLocally,
    cacheReferenceImages,
    syncPending,
  };
};

export default useOfflineSync;
